import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Eye, 
  Download, 
  ExternalLink, 
  X,
  AlertCircle,
  Loader2,
  FileText,
  Image as ImageIcon,
  Video,
  File
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

interface FilePreviewProps {
  resourceType: 'trainingplans' | 'activities' | 'users' | 'jobroles' | 'procedures' | 'policies' | 'safety-incidents';
  resourceId: string;
  attachment: {
    id: string;
    filename: string;
    mimetype: string;
    size: number;
    url: string;
    key: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

// resourceType as exposed to callers → API path segment(s).
const getResourcePath = (resourceType: FilePreviewProps['resourceType']): string =>
  resourceType === 'safety-incidents' ? 'safety/incidents' : resourceType;

interface PreviewData {
  previewUrl: string;
  previewType: 'image' | 'pdf' | 'video' | 'document' | 'download';
  filename: string;
  mimetype: string;
  size: number;
  expiresIn: number;
}

export default function FilePreview({ resourceType, resourceId, attachment, isOpen, onClose }: FilePreviewProps) {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate preview URL when modal opens
  React.useEffect(() => {
    if (isOpen && !previewData && !loading) {
      generatePreviewUrl();
    }
  }, [isOpen]);

  const generatePreviewUrl = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/${getResourcePath(resourceType)}/${resourceId}/attachments/${attachment.id}/preview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setPreviewData(result);
      } else {
        setError(result.message || 'Failed to generate preview');
        if (result.previewType === 'download') {
          // File not previewable, offer download instead
          handleDirectDownload();
        }
      }
    } catch (error) {
      console.error('Preview error:', error);
      setError('Failed to load preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDirectDownload = async () => {
    try {
      // Generate a fresh signed URL for download
      const response = await fetch(`${API_BASE}/api/${getResourcePath(resourceType)}/${resourceId}/attachments/${attachment.id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const result = await response.json();

      if (result.success && result.downloadUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = attachment.filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        onClose();
      } else {
        toast.error('Failed to generate download link. Please try again.');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Please try again.');
    }
  };

  const handleOpenInNewTab = () => {
    if (previewData?.previewUrl) {
      window.open(previewData.previewUrl, '_blank');
      onClose();
    }
  };

  const handleClose = () => {
    setPreviewData(null);
    setError(null);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to get a clean file type display
  const getFileTypeDisplay = (mimetype: string): string => {
    const typeMap: { [key: string]: string } = {
      'application/pdf': 'PDF',
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'image/jpeg': 'JPG',
      'image/jpg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/webp': 'WebP',
      'video/mp4': 'MP4',
      'video/webm': 'WebM',
      'video/ogg': 'OGG',
      'text/plain': 'TXT',
    };

    return typeMap[mimetype] || mimetype.split('/')[1]?.toUpperCase() || 'FILE';
  };

  // Helper function to truncate filename for display
  const getTruncatedFilename = (filename: string, maxLength: number = 40): string => {
    if (filename.length <= maxLength) return filename;
    
    const extension = filename.split('.').pop();
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const maxNameLength = maxLength - (extension ? extension.length + 4 : 3); // Account for "..." and "."
    
    if (extension) {
      return `${nameWithoutExt.substring(0, maxNameLength)}...${extension}`;
    } else {
      return `${filename.substring(0, maxLength - 3)}...`;
    }
  };

  const getFileIcon = (previewType: string) => {
    switch (previewType) {
      case 'image':
        return <ImageIcon className="w-5 h-5" />;
      case 'pdf':
        return <FileText className="w-5 h-5" />;
      case 'video':
        return <Video className="w-5 h-5" />;
      case 'document':
        return <FileText className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin mb-4 text-blue-600" />
          <p className="text-sm text-gray-500 text-center px-4">Generating secure preview...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12 min-h-[300px] px-4">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-sm text-red-600 mb-6 text-center max-w-md">{error}</p>
          <Button onClick={handleDirectDownload} variant="outline" size="lg">
            <Download className="w-4 h-4 mr-2" />
            Download Instead
          </Button>
        </div>
      );
    }

    if (!previewData) {
      return null;
    }

    switch (previewData.previewType) {
      case 'image':
        return (
          <div className="flex items-center justify-center p-4 min-h-0">
            <img
              src={previewData.previewUrl}
              alt={previewData.filename}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
              style={{ maxHeight: 'calc(95vh - 200px)' }}
              onError={() => setError('Failed to load image')}
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="w-full h-full min-h-0">
            <div 
              className="w-full border rounded-lg overflow-hidden bg-white"
              style={{ height: 'calc(95vh - 200px)' }}
            >
              <iframe
                src={previewData.previewUrl}
                className="w-full h-full border-0"
                title={previewData.filename}
                onError={() => setError('Failed to load PDF')}
              />
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="flex items-center justify-center p-4 min-h-0">
            <video
              controls
              className="max-w-full max-h-full rounded-lg shadow-lg"
              style={{ maxHeight: 'calc(95vh - 200px)' }}
              onError={() => setError('Failed to load video')}
            >
              <source src={previewData.previewUrl} type={previewData.mimetype} />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'document':
        return (
          <div className="w-full h-full min-h-0">
            <div 
              className="w-full border rounded-lg overflow-hidden bg-white"
              style={{ height: 'calc(95vh - 250px)' }}
            >
              <iframe
                src={previewData.previewUrl}
                className="w-full h-full border-0"
                title={previewData.filename}
                onError={() => setError('Failed to load document')}
              />
            </div>
            <div className="p-3 bg-yellow-50 border border-t-0 rounded-b-lg">
              <p className="text-sm text-yellow-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Some document formats may not display correctly in the browser.</span>
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center py-12 min-h-[300px] px-4">
            <File className="w-16 h-16 text-gray-400 mb-6" />
            <p className="text-sm text-gray-500 mb-6 text-center max-w-md">
              This file type cannot be previewed in the browser
            </p>
            <Button onClick={handleDirectDownload} size="lg">
              <Download className="w-4 h-4 mr-2" />
              Download File
            </Button>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-7xl w-[95vw] max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0 pb-4">
          {/* Mobile-first responsive header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* File info section */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {previewData && getFileIcon(previewData.previewType)}
              <div className="min-w-0 flex-1">
                <DialogTitle 
                  className="text-left text-base sm:text-lg truncate pr-2" 
                  title={attachment.filename}
                >
                  <span className="hidden sm:inline">{attachment.filename}</span>
                  <span className="sm:hidden">{getTruncatedFilename(attachment.filename, 25)}</span>
                </DialogTitle>
                <DialogDescription className="flex flex-wrap items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {getFileTypeDisplay(attachment.mimetype)}
                  </Badge>
                  <span className="text-xs text-gray-500">{formatFileSize(attachment.size)}</span>
                  {previewData && (
                    <span className="text-xs text-green-600">
                      <span className="hidden sm:inline">Preview expires in </span>
                      {Math.floor(previewData.expiresIn / 60)}
                      <span className="hidden sm:inline"> min</span>
                      <span className="sm:hidden">min</span>
                    </span>
                  )}
                </DialogDescription>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {previewData && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleOpenInNewTab}
                  className="hidden sm:flex"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  New Tab
                </Button>
              )}
              {previewData && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleOpenInNewTab}
                  className="sm:hidden"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDirectDownload}
                className="hidden sm:flex"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDirectDownload}
                className="sm:hidden"
              >
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Content area with proper responsive sizing */}
        <div className="flex-1 overflow-auto min-h-0">
          {renderPreviewContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
} 